"use client";

import { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../../constants/config";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw } from "lucide-react";

export default function StudentSubjectManager() {
  const [students, setStudents] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
 
  const [togglingId, setTogglingId] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState("");
  const [error, setError] = useState("");


  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const res = await axiosInstance.get(API_ENDPOINTS.usersStudents);
        setStudents(res.data.data?.students ?? []);
      } catch (err) {
        setError("Failed to load students: " + (err.response?.data?.message ?? err.message));
      } finally {
        setLoadingStudents(false);
      }
    };
    fetchStudents();
  }, []);

  const fetchSubjects = useCallback(async (studentId) => {
    setLoadingSubjects(true);
    setError("");
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.studentSubjects(studentId));
      const subjects = res.data.data?.subjects ?? [];
      console.log('[StudentSubjectManager] raw API response:', res.data);
      console.log('[StudentSubjectManager] subjects with types:', subjects.map(s => ({
        name: s.subject?.name,
        subjectType: s.subjectType,
        status: s.status,
        _id: s._id
      })));
      setSubjects(subjects);
    } catch (err) {
      setError("Failed to load subjects: " + (err.response?.data?.message ?? err.message));
    } finally {
      setLoadingSubjects(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedStudentId) return;
    fetchSubjects(selectedStudentId);
  }, [selectedStudentId, fetchSubjects]);

  const handleSync = async () => {
    setSyncing(true);
    setError("");
    setSyncMessage("");
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.studentSubjectsSync, { studentId: selectedStudentId });
      const assigned = res.data.data?.assigned ?? 0;
      setSyncMessage(assigned > 0 ? `Synced — ${assigned} subject(s) assigned.` : "Sync complete. No new subjects found. Make sure subjects are assigned to this student's section first (Settings → Section Subjects).");
      await fetchSubjects(selectedStudentId);
    } catch (err) {
      setError("Sync failed: " + (err.response?.data?.message ?? err.message));
    } finally {
      setSyncing(false);
    }
  };

  const handleToggle = async (enrollmentId, currentStatus) => {
    setTogglingId(enrollmentId);
    const endpoint =
      currentStatus === "active"
        ? API_ENDPOINTS.studentSubjectDrop(enrollmentId)
        : API_ENDPOINTS.studentSubjectRestore(enrollmentId);
    try {
      await axiosInstance.patch(endpoint);
      setSubjects((prev) =>
        prev.map((s) =>
          s._id === enrollmentId
            ? { ...s, status: currentStatus === "active" ? "dropped" : "active" }
            : s
        )
      );
    } catch (err) {
      setError("Failed to update subject: " + (err.response?.data?.message ?? err.message));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Card className="max-w-xl mx-auto mt-8">
      <CardHeader>
        <CardTitle>Student Subject Management</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {loadingStudents ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading students...</span>
          </div>
        ) : (
          <Select onValueChange={setSelectedStudentId} value={selectedStudentId}>
            <SelectTrigger>
              <SelectValue placeholder="Select a student" />
            </SelectTrigger>
            <SelectContent>
              {students.map((s) => {
                const fullName = [s.firstName, s.middleName, s.lastName].filter(Boolean).join(' ') || s.email || s._id;
                const identifier = s.admissionNumber ?? s.rollNumber ?? s.email;
                return (
                  <SelectItem key={s._id} value={s._id}>
                    {fullName}{identifier ? ` — ${identifier}` : ''}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}


        {selectedStudentId && (
          <>
            <div className="flex items-center justify-between">
              <Button
                size="sm"
                variant="outline"
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                {syncing ? "Syncing..." : "Sync from Section"}
              </Button>
            </div>
            {syncMessage && <p className="text-sm text-green-600">{syncMessage}</p>}
            {loadingSubjects ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading subjects...</span>
              </div>
            ) : subjects.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No subjects assigned. Click "Sync from Section" to assign subjects from this student's section.
              </p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{subjects.length} subject(s)</p>
                </div>
                <ul className="space-y-3">
                  {subjects.map((enrollment) => {
                    const isActive = enrollment.status === "active";
                    const isToggling = togglingId === enrollment._id;
                    const subjectTypeLabel = { core: 'Core', elective: 'Elective', optional: 'Optional' }[enrollment.subjectType] ?? 'Elective';
                    return (
                      <li
                        key={enrollment._id}
                        className="flex items-center justify-between rounded-md border px-4 py-3"
                      >
                        <div>
                          <p className="font-medium">{enrollment.subject?.name}</p>
                          <p className="text-xs text-muted-foreground">{enrollment.subject?.code}</p>
                          <Badge variant="secondary" className="mt-2 text-xs">
                            {subjectTypeLabel}
                          </Badge>
                        </div>
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          {isToggling ? (
                            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                          ) : (
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-primary"
                              checked={isActive}
                              onChange={() => handleToggle(enrollment._id, enrollment.status)}
                            />
                          )}
                          <span className="text-sm text-muted-foreground">
                            {isActive ? "Active" : "Dropped"}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}